import { useEffect, useRef, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { tokenStore } from "../api/token";
import type { PresenceViewer } from "../api/endpoints";
import { useSession } from "./useSession";

export type LaunchEventType =
  | "card.created"
  | "card.updated"
  | "card.moved"
  | "card.deleted"
  | "launch.updated"
  | "launch.deleted"
  | "day.created"
  | "day.updated"
  | "day.deleted"
  | "landing.created"
  | "landing.updated"
  | "landing.deleted"
  | "template.applied"
  | "presence.card";

export interface LaunchEvent {
  type: LaunchEventType;
  launchId: string;
  entityId: string;
  by: { id: string; email: string; name: string | null };
  at: string;
  data?: unknown;
}

// Opens a long-lived SSE connection to /api/v1/launches/:id/events and
// reconciles the TanStack Query cache when peers mutate shared state.
// Self-originated events are ignored — the actor's own mutation already
// invalidated the right keys via its onSettled handler.
export function useLaunchEvents(launchId: string | null | undefined): void {
  const qc = useQueryClient();
  const session = useSession();
  const currentUserId = session.data?.id ?? null;
  const userIdRef = useRef(currentUserId);
  userIdRef.current = currentUserId;

  // Subscribe reactively to the token so logout/role-change tears down the
  // EventSource instead of letting it 401-loop server-side.
  const token = useSyncExternalStore(
    tokenStore.subscribe,
    () => tokenStore.get(),
    () => null,
  );

  useEffect(() => {
    if (!launchId) return;
    // Dev bypass allows connections without JWT, but in prod without a token
    // the server returns 401 — don't open the socket in that case.
    if (!token && import.meta.env.PROD) return;

    // In dev bypass there is no JWT — server accepts the connection without
    // the query param thanks to AUTH_DEV_BYPASS.
    const url = token
      ? `/api/v1/launches/${launchId}/events?access_token=${encodeURIComponent(token)}`
      : `/api/v1/launches/${launchId}/events`;

    const source = new EventSource(url, { withCredentials: false });

    const handle = (raw: MessageEvent) => {
      let event: LaunchEvent;
      try {
        event = JSON.parse(raw.data);
      } catch {
        return;
      }
      // Skip self for data events — our mutation already handled the cache.
      // Presence events carry authoritative snapshots and must always apply.
      if (
        event.type !== "presence.card" &&
        event.by.id &&
        userIdRef.current &&
        event.by.id === userIdRef.current
      ) {
        return;
      }

      switch (event.type) {
        case "card.created":
        case "card.updated":
        case "card.moved":
        case "card.deleted":
        case "template.applied": {
          qc.invalidateQueries({ queryKey: ["cards", event.launchId] });
          break;
        }
        case "launch.updated":
        case "launch.deleted": {
          qc.invalidateQueries({ queryKey: ["launch", event.launchId] });
          qc.invalidateQueries({ queryKey: ["launches"] });
          break;
        }
        case "day.created":
        case "day.updated":
        case "day.deleted": {
          qc.invalidateQueries({ queryKey: ["launch", event.launchId] });
          qc.invalidateQueries({ queryKey: ["cards", event.launchId] });
          break;
        }
        case "landing.created":
        case "landing.updated":
        case "landing.deleted": {
          qc.invalidateQueries({ queryKey: ["landings"] });
          qc.invalidateQueries({ queryKey: ["cards", event.launchId] });
          break;
        }
        case "presence.card": {
          const viewers = (event.data as { viewers?: PresenceViewer[] } | undefined)?.viewers ?? [];
          qc.setQueryData<Record<string, PresenceViewer[]>>(
            ["presence", "launch", event.launchId],
            (prev) => {
              const next = { ...(prev ?? {}) };
              if (viewers.length === 0) delete next[event.entityId];
              else next[event.entityId] = viewers;
              return next;
            },
          );
          break;
        }
      }
    };

    // The server tags events via `event:` field so we subscribe per type; we
    // also attach a generic `message` listener as a fallback if the event
    // type isn't recognised by EventSource.
    const types: LaunchEventType[] = [
      "card.created",
      "card.updated",
      "card.moved",
      "card.deleted",
      "launch.updated",
      "launch.deleted",
      "day.created",
      "day.updated",
      "day.deleted",
      "landing.created",
      "landing.updated",
      "landing.deleted",
      "template.applied",
      "presence.card",
    ];
    for (const t of types) {
      source.addEventListener(t, handle as EventListener);
    }
    source.addEventListener("message", handle);

    return () => {
      for (const t of types) {
        source.removeEventListener(t, handle as EventListener);
      }
      source.removeEventListener("message", handle);
      source.close();
    };
  }, [launchId, qc, token]);
}
